import React from "react";

import InfoCircleIcon from "@uui/InfoCircle";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import { SelectChangeEvent, Typography } from "@mui/material";

import UpdateButtons from "@app/analytics/account/components/UpdateButtons";
import useUpdateFirmographics, {
  FirmographicsFieldValues,
} from "@app/analytics/hooks/settings/useUpdateFirmographics";
import { JobDepartment, JobRoleLevel } from "@app/analytics/types";
import { DEPARTMENTS, ROLE_LEVELS } from "@app/common/constants/firmographics";
import { showToast } from "@app/common/hooks/useToasts";
import { User } from "@app/common/types";
import { yupResolver } from "@app/utils/forms/yupResolver";

import Dropdown, { DropdownMenuItem } from "@rui/Dropdown";
import { getSelectedFromOptions } from "@rui/Dropdown/utils";
import Stack, { Row } from "@rui/Stack";
import TextField from "@rui/TextField";

const firmographicsValidationSchema = yup.object().shape({
  jobTitle: yup.string().required("Job title is required to continue"),
  jobDepartment: yup.string().required("Department is required to continue"),
  jobRoleLevel: yup.string().required("Role level is required to continue"),
});

const FirmographicsForm = ({
  cancelText,
  saveText,
  disclaimer,
  onCloseForm,
  onSuccess,
  defaultValues,
}: {
  onCloseForm: () => void;
  onSuccess?: () => void;
  cancelText?: string;
  saveText?: string;
  disclaimer?: string;
  defaultValues?: {
    jobRoleLevel: User["jobRoleLevel"];
    jobDepartment: User["jobDepartment"];
    jobTitle: User["jobTitle"];
  };
}) => {
  const form = useForm<FirmographicsFieldValues>({
    resolver: yupResolver(firmographicsValidationSchema),
    mode: "onBlur",
    defaultValues,
  });

  const updateFirmographics = useUpdateFirmographics();

  const handleSave = (data: FirmographicsFieldValues) => {
    updateFirmographics.mutate(data, {
      onSuccess: () => {
        showToast({
          severity: "success",
          message: "Information successfully updated!",
        });
        onSuccess?.();
        onCloseForm();
      },
      onError: () => {
        showToast({
          severity: "error",
          message:
            "Something went wrong updating your info. Please try again later.",
        });
      },
    });
  };

  const handleChangeDropdown =
    (id: "jobRoleLevel" | "jobDepartment") => (e: SelectChangeEvent) => {
      const newValue = e.target.value as JobRoleLevel | JobDepartment;
      form.setValue(id, newValue);
      form.clearErrors(id);
    };

  const selectedRoleLevel = form.watch("jobRoleLevel");
  const selectedDepartment = form.watch("jobDepartment");
  const errors = form.formState.errors;

  return (
    <>
      <Stack spacing={1} flex={1} color="shades.neutral.800">
        <Typography variant="bodyPrimaryMed">Job title</Typography>
        <TextField
          placeholder="e.g., Product Designer"
          sx={{
            minWidth: 0,
            ".MuiOutlinedInput-root": {
              minHeight: "40px",
            },
            ".MuiOutlinedInput-input": {
              fontSize: "0.875rem",
            },
          }}
          error={Boolean(errors.jobTitle)}
          helperText={errors.jobTitle?.message}
          {...form.register("jobTitle")}
        />
        <Typography variant="bodyPrimaryMed">Role level</Typography>
        <Dropdown
          placeholder="Select one"
          name="role-level"
          onChange={handleChangeDropdown("jobRoleLevel")}
          value={selectedRoleLevel ?? ""}
          selectedItem={getSelectedFromOptions(ROLE_LEVELS, selectedRoleLevel)}
          error={Boolean(errors.jobRoleLevel)}
          inputProps={{ helperText: errors.jobRoleLevel?.message }}
        >
          {ROLE_LEVELS.map((jobRoleLevel) => (
            <DropdownMenuItem
              key={jobRoleLevel.value}
              selected={jobRoleLevel.value === selectedRoleLevel}
              {...jobRoleLevel}
            />
          ))}
        </Dropdown>
        <Typography variant="bodyPrimaryMed">Your department</Typography>
        <Dropdown
          placeholder="Select one"
          name="jobDepartment"
          onChange={handleChangeDropdown("jobDepartment")}
          value={selectedDepartment ?? ""}
          selectedItem={getSelectedFromOptions(DEPARTMENTS, selectedDepartment)}
          error={Boolean(errors.jobDepartment)}
          inputProps={{ helperText: errors.jobDepartment?.message }}
        >
          {DEPARTMENTS.map((jobDepartment) => (
            <DropdownMenuItem
              key={jobDepartment.value}
              selected={jobDepartment.value === selectedDepartment}
              {...jobDepartment}
            />
          ))}
        </Dropdown>
      </Stack>
      {disclaimer && (
        <Row variant="left" spacing={1} sx={{ color: "shades.neutral.400" }}>
          <InfoCircleIcon fontSize="inherit" />
          <Typography variant="labelReg">{disclaimer}</Typography>
        </Row>
      )}
      <UpdateButtons
        cancelLabel={cancelText}
        loading={updateFirmographics.isPending}
        onCancel={onCloseForm}
        onSubmit={form.handleSubmit(handleSave)}
        saveLabel={saveText}
      />
    </>
  );
};

export default FirmographicsForm;
